import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { QuestionRenderer } from "modules/questions/QuestionRenderer";
import { useTranslation } from "shared/contexts/I18nContext";
import { useLoading } from "shared/contexts/Loading";
import apiCall from "shared/services/ApiAdapter";
import { ProtectedVisibility } from "shared/security/ProtectedRoute";
import { loadMathJaxScript } from "modules/assessment/loadMathJaxScript";
import { convertMathJax, typesetMath } from "modules/assessment/AssessmentApp";

export const QuestionMetricsPanel = ({
    assignment,
    displayToast,
    questions,
    onListView,
    enableEditing = false,
    disableEditingReason = "",
}) => {
    const { t } = useTranslation();
    const { startLoading, stopLoading } = useLoading();
    const [questionMetrics, setQuestionMetrics] = useState([]);
    const [editedMetrics, setEditedMetrics] = useState({});
    const [savingMetricId, setSavingMetricId] = useState(null);
    const optionsRef = useRef(null);

    useEffect(() => {
        if (!assignment?.id) return;
        loadMathJaxScript();

        const fetchMetrics = async () => {
            try {
                const data = await apiCall(

                    `/question-metrics?assignmentId.equals=${assignment.id}&isActive.equals=true`,
                    false,
                    "GET",
                    null,
                    startLoading,
                    stopLoading,
                    displayToast
                );
                
                // Deduplicate metrics by questionCode to avoid repeating questions
                if (Array.isArray(data)) {
                    const uniqueMetrics = [];
                    const seenCodes = new Set();
                    // Sort descending by id to pick the latest metric in case of duplicates
                    const sortedData = [...data].sort((a, b) => (b.id || 0) - (a.id || 0));
                    
                    for (const metric of sortedData) {
                        if (metric.questionCode && !seenCodes.has(metric.questionCode)) {
                            seenCodes.add(metric.questionCode);
                            uniqueMetrics.push(metric);
                        } else if (!metric.questionCode) {
                            // If no question code, just push it (fallback)
                            uniqueMetrics.push(metric);
                        }
                    }
                    // Reverse to restore original ascending id order
                    setQuestionMetrics(uniqueMetrics.reverse());
                } else {
                    setQuestionMetrics(data);
                }
                
                // const codes = Array.isArray(data)
                //     ? data.map((m) => m.questionCode).filter(Boolean)
                //     : [];
                // setQuestionCodes(codes);
            } catch (err) {
                // displayToast({ type: "error", message: t("failedToLoadMetrics") });
                setQuestionMetrics([]);
            }
        };

        fetchMetrics();
    }, [assignment?.id]);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (optionsRef.current && typeof typesetMath === 'function') {
                typesetMath(optionsRef.current);
            }
        }, 100);
        return () => clearTimeout(timeoutId);
    }, [questions, questionMetrics]);

    useEffect(() => {
        if (!Array.isArray(questionMetrics)) return;

        const initialEdits = {};
        if (!enableEditing) {
            setEditedMetrics({});
            return;
        }
        questionMetrics.forEach((metric) => {
            const rawStatus =
                metric?.status ??
                metric?.reviewStatus ??
                metric?.questionStatus ??
                metric?.state ??
                "";
            const status =
                rawStatus === null || rawStatus === undefined
                    ? ""
                    : String(rawStatus).toUpperCase();
            initialEdits[metric?.id] = {
                status:
                    status ||
                    (metric?.isCorrect === true ? "CORRECT" : "INCORRECT"),
            };
        });
        setEditedMetrics(initialEdits);
    }, [questionMetrics, enableEditing]);

    const statusOptions = useMemo(
        () => ["CORRECT", "INCORRECT"],
        []
    );

    const handleFieldChange = useCallback((metricId, field, value) => {
        if (!enableEditing) return;
        setEditedMetrics((prev) => ({
            ...prev,
            [metricId]: {
                ...prev[metricId],
                [field]: value,
            },
        }));
    }, [enableEditing]);

    const handleSaveMetric = useCallback(
        async (metric) => {
            if (!enableEditing) return;
            const metricId = metric?.id;
            if (!metricId) return;
            const edits = editedMetrics[metricId] || {};
            const rawOriginalStatus =
                metric?.status ??
                metric?.reviewStatus ??
                metric?.questionStatus ??
                metric?.state ??
                "";
            const originalStatus =
                rawOriginalStatus === null || rawOriginalStatus === undefined
                    ? ""
                    : String(rawOriginalStatus);
            const editedStatus =
                edits.status === null || edits.status === undefined
                    ? ""
                    : String(edits.status);
            const payload = {};

            if (editedStatus !== originalStatus) {
                payload.status = editedStatus === "" ? null : editedStatus;
                payload.isCorrect = editedStatus === "CORRECT";
            }
            payload.id = metricId;
            if (Object.keys(payload).length === 0) {
                if (typeof displayToast === "function") {
                    displayToast("No changes to save", "info");
                }
                return;
            }

            try {
                setSavingMetricId(metricId);
                const updated = await apiCall(
                    `/question-metrics/${metricId}`,
                    false,
                    "PATCH",
                    payload,
                    startLoading,
                    stopLoading,
                    displayToast
                );
                // Calculate updated metrics for score calculation
                const updatedMetrics = questionMetrics.map((item) =>
                    item.id === metricId
                        ? {
                            ...item,
                            ...payload,
                            ...(updated || {}),
                            status:
                                updated?.status ??
                                updated?.reviewStatus ??
                                updated?.questionStatus ??
                                updated?.state ??
                                payload.status ??
                                item.status ??
                                "",
                            isCorrect:
                                updated?.isCorrect ??
                                payload.isCorrect ??
                                item.isCorrect ??
                                false,
                        }
                        : item
                );

                // Recalculate assignment score based on updated question metrics
                // Score = number of questions marked as correct (equal weightage)
                const newScore = updatedMetrics.filter(
                    (m) => m.isCorrect === true
                ).length;

                setQuestionMetrics(updatedMetrics);
                setEditedMetrics((prev) => ({
                    ...prev,
                    [metricId]: {
                        status:
                            updated?.status ??
                            updated?.reviewStatus ??
                            updated?.questionStatus ??
                            updated?.state ??
                            (payload.status === null
                                ? ""
                                : payload.status ??
                                prev[metricId]?.status ??
                                ""),
                    },
                }));

                // Update assignment score
                if (assignment?.id && newScore !== undefined) {
                    try {
                        await apiCall(
                            `/assignments/${assignment.id}`,
                            false,
                            "PATCH",
                            {
                                id: assignment.id,
                                score: newScore,
                            },
                            startLoading,
                            stopLoading,
                            displayToast
                        );
                    } catch (scoreErr) {
                        console.error(
                            "Failed to update assignment score:",
                            scoreErr
                        );
                        if (typeof displayToast === "function") {
                            displayToast(
                                "Question updated but failed to update assignment score",
                                "warning"
                            );
                        }
                    }
                }

                if (typeof displayToast === "function") {
                    displayToast("Question metric updated", "success");
                }
            } catch (err) {
                if (typeof displayToast === "function") {
                    displayToast(
                        `Failed to update question metric: ${err.message}`,
                        "error"
                    );
                }
            } finally {
                setSavingMetricId(null);
            }
        },
        [
            displayToast,
            editedMetrics,
            enableEditing,
            startLoading,
            stopLoading,
            questionMetrics,
            assignment,
        ]
    );

    return (
        <div ref={optionsRef} className="flex-1 overflow-y-auto bg-white dark:bg-gray-900 px-6 py-4 relative">
            {/* Header */}
            <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-3 mb-6 border-b dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
                <span className="text-gray-900 dark:text-gray-100">
                    <span className="text-2lg">Evaluation </span>
                    <span className="font-bold">{assignment?.evaluation?.name} </span>
                    <span className="text-2lg">for </span>
                    <span className="text-2lg font-bold"> {assignment?.student?.name}</span>
                </span>
                <button
                    onClick={onListView}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-blue-500 dark:hover:bg-blue-600"
                >
                    <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                    </svg>
                    {t("back") ?? "Back to list"}
                </button>
            </div>

            {/* Metrics */}
            {questionMetrics.length === 0 || questions?.length === 0 ? (
                <p className="text-center text-gray-500 dark:text-gray-400 mt-8">
                    {t("noMetricsAvailable")}
                </p>
            ) : (
                questionMetrics.map((metric, index) => {
                    const question = questions?.find((q) => q.code === metric.questionCode);
                    const isCorrect = metric.isCorrect;
                    const rawOriginalStatus =
                        metric?.status ??
                        metric?.reviewStatus ??
                        metric?.questionStatus ??
                        metric?.state ??
                        "";
                    const originalStatus =
                        rawOriginalStatus === null || rawOriginalStatus === undefined
                            ? ""
                            : String(rawOriginalStatus);
                    const editedStatus =
                        editedMetrics[metric.id]?.status === null ||
                            editedMetrics[metric.id]?.status === undefined
                            ? ""
                            : String(editedMetrics[metric.id]?.status);
                    const hasChanges =
                        editedStatus !== originalStatus;
                    const editingLocked =
                        assignment?.status === "Completed" ||
                        assignment?.status === "COMPLETED";
                    const editingDisabled = !enableEditing || editingLocked;

                    return (
                        <div
                            key={index}
                            className={`rounded-lg border shadow-sm p-5 transition-shadow ${isCorrect
                                ? "border-green-300 bg-green-50 dark:border-green-700 dark:bg-green-900/30"
                                : "border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30"
                                } hover:shadow-md`}
                        >
                            <div className="grid grid-cols-12 gap-6 mb-6">
                                {/* Left: Question */}
                                <div className="col-span-12 lg:col-span-8">
                                    <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                        {t("question")} #{index + 1}
                                    </h4>
                                    {question && <QuestionRenderer question={question} index={index} />}
                                    
                                    {question?.answerChoices?.length > 0 && (
                                        <div className="mt-4">
                                            <p className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-2">Options:</p>
                                            <ul className="list-none space-y-2">
                                                {question.answerChoices.map((choice, cIndex) => {
                                                    // Optional: Map index to A, B, C, D
                                                    const letter = String.fromCharCode(65 + cIndex);
                                                    return (
                                                        <li 
                                                            key={cIndex} 
                                                            className={`flex items-start text-sm p-2 rounded-md ${
                                                                choice.isCorrect 
                                                                    ? 'bg-green-100/50 text-green-700 dark:bg-green-900/30 dark:text-green-300 border border-green-200 dark:border-green-800' 
                                                                    : 'bg-gray-50 text-gray-700 dark:bg-gray-800 dark:text-gray-300 border border-gray-200 dark:border-gray-700'
                                                            }`}
                                                        >
                                                            <span className="font-bold mr-3">{letter}.</span>
                                                            <span className="flex-1">{convertMathJax(choice.choiceText)}</span>
                                                            {choice.isCorrect && (
                                                                <svg className="w-5 h-5 ml-2 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
                                                            )}
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        </div>
                                    )}
                                </div>

                                {/* Right: Metrics */}
                                <div className="col-span-12 lg:col-span-4">
                                    <div className="space-y-3 text-sm text-gray-700 dark:text-gray-300">
                                        <div className="flex justify-between">
                                            <span className="font-medium">{t("correct")}:</span>
                                            <span
                                                className={`font-semibold ${isCorrect
                                                    ? "text-green-600 dark:text-green-400"
                                                    : "text-red-600 dark:text-red-400"
                                                    }`}
                                            >
                                                {isCorrect ? t("yes") : t("no")}
                                            </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="font-medium">{t("selectedAnswer")}:</span>
                                            <span>
                                                {convertMathJax(metric.selectedAnswer ?? t("none"))}
                                            </span>
                                        </div>
                                        {!isCorrect && <div className="flex justify-between">
                                            <span className="font-medium">{t("correctAnswer")}:</span>
                                            <span>{convertMathJax(question?.answerChoices?.find((choice) => choice.isCorrect)
                                                ?.choiceText ?? t("none"))}</span>
                                        </div>
                                        }

                                        <ProtectedVisibility requiredRoles={["ROLE_TEACHER"]}>
                                            <div className="flex justify-between">
                                                <span className="font-medium">{t("timeSpent")}:</span>
                                                <span>{metric.timeSpentSeconds}s</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="font-medium">{t("attempts")}:</span>
                                                <span>{metric.attempts ?? 0}</span>
                                            </div>
                                        </ProtectedVisibility>
                                    </div>
                                </div>
                            </div>
                            {enableEditing && !editingDisabled && (
                                <ProtectedVisibility requiredRoles={["ROLE_TEACHER"]}>
                                    <div className="mt-4 border-t border-gray-200 dark:border-gray-700 pt-4 space-y-4">
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide mb-1">
                                                {t("status") ?? "Status"}
                                            </label>
                                            <select
                                                className="w-full max-w-xs px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100"
                                                value={
                                                    editedMetrics[metric.id]?.status ??
                                                    ""
                                                }
                                                onChange={(e) =>
                                                    handleFieldChange(
                                                        metric.id,
                                                        "status",
                                                        e.target.value
                                                    )
                                                }
                                            >
                                                <option value="">
                                                    {t("select") ?? "Select"}
                                                </option>
                                                {statusOptions.map((status) => (
                                                    <option key={status} value={status}>
                                                        {status}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="flex justify-end gap-2">
                                            <button
                                                type="button"
                                                className="px-4 py-2 text-sm font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
                                                onClick={() => handleSaveMetric(metric)}
                                                disabled={
                                                    savingMetricId === metric.id || !hasChanges
                                                }
                                            >
                                                {savingMetricId === metric.id
                                                    ? t("saving") ?? "Saving..."
                                                    : t("save") ?? "Save"}
                                            </button>
                                        </div>
                                    </div>
                                </ProtectedVisibility>
                            )}
                            {enableEditing && editingDisabled && (
                                <div className="mt-4 border-t border-gray-200 dark:border-gray-700 pt-4">
                                    <div className="text-sm text-gray-500 dark:text-gray-400">
                                        {disableEditingReason ||
                                            t("questionStatusLocked") ||
                                            "Question status updates are disabled for completed assignments."}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })
            )}
        </div>
    );
};
